import React, { useCallback } from 'react';
import { useStore, EdgeLabelRenderer } from 'reactflow';
import { Tooltip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import useCanvasStore from '../../useCanvasStore';

const NODE_WIDTH = 250;
const NODE_HEIGHT = 80;
const BOX_PADDING = 40;

/**
 * SplitConvergeLink - Visual edge connecting loop_through_list to converge_threads
 * Renders a dashed bounding box around all components in the parallel region
 */
const SplitConvergeLink = ({ id, source, target }) => {
    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);

    const sourceNode = useStore(useCallback(store => store.nodeInternals.get(source), [source]));
    const targetNode = useStore(useCallback(store => store.nodeInternals.get(target), [target]));

    if (!sourceNode || !targetNode) return null;

    // Find all nodes that are between source and target (in the parallel region)
    // We'll calculate bounds based on all nodes reachable from source before reaching target
    const getNodesInParallelRegion = () => {
      const visited = new Set();
      const regionNodes = [sourceNode, targetNode];
      const queue = [source];
      
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId) || currentId === target) continue;
        visited.add(currentId);
        
        // Find outgoing edges from current node
        const outgoingEdges = edges.filter(e => e.source === currentId && e.type !== 'splitConvergeLink');
        for (const edge of outgoingEdges) {
          const nextNode = nodes.find(n => n.id === edge.target);
          if (nextNode && !visited.has(edge.target)) {
            regionNodes.push(nextNode);
            if (edge.target !== target) {
              queue.push(edge.target);
            }
          }
        }
      }
      
      return regionNodes;
    };

    const regionNodes = getNodesInParallelRegion();

    // Calculate bounding box for all nodes in the parallel region
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    for (const node of regionNodes) {
      if (!node?.position) continue;
      const x = node.position.x;
      const y = node.position.y;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + NODE_WIDTH);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + NODE_HEIGHT);
    }

    // Add padding (extra 12px on top to account for label overlapping into box)
    const LABEL_OVERLAP = 12;
    const boxLeft = minX - BOX_PADDING;
    const boxTop = minY - BOX_PADDING - LABEL_OVERLAP;
    const boxWidth = (maxX - minX) + (BOX_PADDING * 2);
    const boxHeight = (maxY - minY) + (BOX_PADDING * 2) + LABEL_OVERLAP;

    // Label position - centered on top border of box
    const labelX = boxLeft + boxWidth / 2;
    const labelY = boxTop; // Sits on the border line

    return (
      <>
        {/* Background box encompassing all parallel components */}
        <rect
          x={boxLeft}
          y={boxTop}
          width={boxWidth}
          height={boxHeight}
          rx={8}
          ry={8}
          fill="rgba(72, 168, 15, 0.06)"
          stroke="rgba(72, 168, 15, 0.5)"
          strokeWidth={2}
          strokeDasharray="6,4"
          style={{ 
            pointerEvents: 'none',
          }}
        />

        {/* Label at top of box */}
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
            }}
          >
            <Tooltip title="Components in this region run in parallel threads" placement="top">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: '#48A80F',
                  color: 'white',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 500,
                  userSelect: 'none',
                  pointerEvents: 'auto',
                }}
              >
                <AccountTreeIcon style={{ fontSize: '14px' }} />
                <span>Parallel Threads</span>
              </div>
            </Tooltip>
          </div>
        </EdgeLabelRenderer>
      </>
    );
};

export default SplitConvergeLink;
